import type { DataProvider, OHLCVRequest, StreamStatus, Unsubscribe } from '../types';
import { ProviderError } from '../types';
import type { Exchange, ExchangeId } from '../../types/exchange';
import type { TradingPair } from '../../types/pair';
import type { OHLCV } from '../../types/ohlcv';
import type { Ticker24h } from '../../types/ticker';
import { timeframeToMs } from '../../utils/timeframe';
import type { ExchangeAdapter } from './types';

export interface MultiExchangeProviderOptions {
  /** Provider id (part of every cache key). Defaults to `exchanges:<ids>`. */
  id?: string;
  /** How long a pair list is reused, ms. Default 10 min. */
  pairsTtlMs?: number;
  /** Poll interval for adapters without a candle stream, ms. Default 10 s. */
  pollIntervalMs?: number;
}

/**
 * One DataProvider over several exchange adapters, routed by `exchangeId`.
 * Adapters without a live stream are polled, so callers can always subscribe.
 */
export function multiExchangeProvider(
  adapters: ExchangeAdapter[],
  options: MultiExchangeProviderOptions = {},
): DataProvider {
  const byId = new Map(adapters.map((adapter) => [adapter.id, adapter]));
  const pairsTtl = options.pairsTtlMs ?? 10 * 60_000;
  const pollMs = options.pollIntervalMs ?? 10_000;
  const pairsCache = new Map<string, { at: number; pairs: Promise<TradingPair[]> }>();

  function adapterFor(exchangeId: ExchangeId): ExchangeAdapter {
    const adapter = byId.get(exchangeId);
    if (!adapter) throw new ProviderError(`Unknown exchange "${exchangeId}"`);
    return adapter;
  }

  function getPairs(exchangeId: ExchangeId): Promise<TradingPair[]> {
    const cached = pairsCache.get(exchangeId);
    if (cached && Date.now() - cached.at < pairsTtl) return cached.pairs;
    const pairs = adapterFor(exchangeId).getPairs();
    pairsCache.set(exchangeId, { at: Date.now(), pairs });
    // A failed load must not be cached.
    pairs.catch(() => pairsCache.delete(exchangeId));
    return pairs;
  }

  return {
    id: options.id ?? `exchanges:${adapters.map((a) => a.id).join(',')}`,

    getOHLCV(request: OHLCVRequest): Promise<OHLCV[]> {
      const { pair, timeframe, from, to } = request;
      return adapterFor(request.exchangeId).getOHLCV({ pair, timeframe, from, to });
    },

    async getExchanges(): Promise<Exchange[]> {
      return adapters.map((adapter) => ({
        id: adapter.id,
        name: adapter.name,
        logoUrl: '',
        supportedFeatures: { websocket: Boolean(adapter.subscribeOHLCV), ohlcv: true, orderbook: false },
      }));
    },

    getPairs,

    async getTicker(exchangeId: ExchangeId, pair: string): Promise<{ last: number }> {
      const tickers = await adapterFor(exchangeId).getTickers();
      const ticker = tickers.find((t) => t.pair === pair);
      if (!ticker) throw new ProviderError(`No ticker for ${pair} on ${exchangeId}`);
      return { last: ticker.last };
    },

    getTickers(exchangeId: ExchangeId): Promise<Ticker24h[]> {
      return adapterFor(exchangeId).getTickers();
    },

    subscribeOHLCV(request, onCandle, onStatus?: (status: StreamStatus) => void): Unsubscribe {
      const adapter = adapterFor(request.exchangeId);
      if (adapter.subscribeOHLCV) {
        return adapter.subscribeOHLCV(request.pair, request.timeframe, onCandle, onStatus);
      }
      let stopped = false;
      const poll = () => {
        const now = Date.now();
        adapter
          .getOHLCV({
            pair: request.pair,
            timeframe: request.timeframe,
            from: now - 2 * timeframeToMs(request.timeframe),
            to: now,
          })
          .then((candles) => {
            if (stopped) return;
            onStatus?.('open');
            for (const candle of candles.slice(-2)) onCandle(candle);
          })
          .catch(() => {
            if (!stopped) onStatus?.('error');
          });
      };
      poll();
      const timer = setInterval(poll, pollMs);
      return () => {
        stopped = true;
        clearInterval(timer);
      };
    },

    subscribeTickers(exchangeId, pairs, onTicker): Unsubscribe {
      const adapter = adapterFor(exchangeId);
      if (adapter.subscribeTickers) return adapter.subscribeTickers(pairs, onTicker);
      let stopped = false;
      const wanted = new Set(pairs);
      const poll = () => {
        adapter
          .getTickers()
          .then((tickers) => {
            if (stopped) return;
            for (const ticker of tickers) if (wanted.has(ticker.pair)) onTicker(ticker);
          })
          .catch(() => {
            // Next poll retries; ticker panels keep their last values meanwhile.
          });
      };
      poll();
      const timer = setInterval(poll, Math.max(pollMs, 15_000));
      return () => {
        stopped = true;
        clearInterval(timer);
      };
    },
  };
}
